// Skills matrix component - shows skills in three columns

import { PublicSkill } from '../../lib/api';

interface SkillsMatrixProps {
  skills?: PublicSkill[];
  isLoading?: boolean;
}

export function SkillsMatrix({ skills, isLoading }: SkillsMatrixProps) {
  if (isLoading) {
    return (
      <section className="section">
        <div className="container-custom">
          <div className="animate-pulse space-y-6">
            <div className="h-10 w-32 bg-gray-200 rounded" />
            <div className="grid md:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="card p-6 space-y-4">
                  <div className="h-6 w-24 bg-gray-200 rounded" />
                  {[1, 2, 3, 4].map((j) => (
                    <div key={j} className="h-4 w-32 bg-gray-200 rounded" />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  const strongSkills = skills?.filter((s) => s.category === 'strong') || [];
  const moderateSkills = skills?.filter((s) => s.category === 'moderate') || [];
  const gapSkills = skills?.filter((s) => s.category === 'gap') || [];

  // Placeholder skills for demo when no data
  const placeholderSkills = {
    strong: [
      'Python',
      'AWS (Lambda, SageMaker, Bedrock, S3)',
      'LLM Evaluation (GPT, Gemini, Claude)',
      'Speech & NLP (Whisper, spaCy)',
      'ML Pipelines (PyTorch, HuggingFace)',
      'Data Analysis & Metrics',
    ],
    moderate: [
      'SQL',
      'Test Automation Frameworks',
      'Statistical Analysis (BLEU, WER, BERTScore)',
      'Git & CI/CD',
      'HTML/CSS',
    ],
    gap: [
      'Frontend Development (React, TypeScript)',
      'Backend Systems Design',
      'Mobile Development',
    ],
  };

  return (
    <section className="section">
      <div className="container-custom">
        {/* Section header */}
        <h2 className="section-title">Skills</h2>
        <p className="section-subtitle">
          An honest self-assessment of my technical abilities.
        </p>

        {/* Skills grid */}
        <div className="grid md:grid-cols-3 gap-6">
          {/* Strong skills */}
          <div className="card border-t-4 border-success">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-success text-xl">✓</span>
              <h3 className="font-serif font-semibold text-lg">Strong</h3>
            </div>
            <ul className="space-y-2">
              {(strongSkills.length > 0
                ? strongSkills.map((s) => s.skill_name)
                : placeholderSkills.strong
              ).map((skill, i) => (
                <li
                  key={i}
                  className="flex items-center gap-2 text-text-secondary"
                >
                  <span className="w-1.5 h-1.5 bg-success rounded-full" />
                  {skill}
                </li>
              ))}
            </ul>
          </div>

          {/* Moderate skills */}
          <div className="card border-t-4 border-text-muted">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-text-muted text-xl">○</span>
              <h3 className="font-serif font-semibold text-lg">Moderate</h3>
            </div>
            <ul className="space-y-2">
              {(moderateSkills.length > 0
                ? moderateSkills.map((s) => s.skill_name)
                : placeholderSkills.moderate
              ).map((skill, i) => (
                <li
                  key={i}
                  className="flex items-center gap-2 text-text-secondary"
                >
                  <span className="w-1.5 h-1.5 bg-text-muted rounded-full" />
                  {skill}
                </li>
              ))}
            </ul>
          </div>

          {/* Gap skills */}
          <div className="card border-t-4 border-warning">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-warning text-xl">✗</span>
              <h3 className="font-serif font-semibold text-lg">Gaps</h3>
            </div>
            <ul className="space-y-2">
              {(gapSkills.length > 0
                ? gapSkills.map((s) => s.skill_name)
                : placeholderSkills.gap
              ).map((skill, i) => (
                <li
                  key={i}
                  className="flex items-center gap-2 text-text-secondary"
                >
                  <span className="w-1.5 h-1.5 bg-warning rounded-full" />
                  {skill}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
